import {
  ZERO,
  beamSpans,
  barLength,
  beats,
  frac,
  fracAdd,
  fracCmp,
  fracDiv,
  fracEq,
  fracMul,
  fracSub,
  fracSum,
  fracToNumber,
  isDyadic,
  isNoteValue,
  noteLength,
  pulseLength,
  type Frac,
  type NoteValue,
  type TimeSignature,
} from './meter';

/**
 * Rhythm theory (topic R4, the T tracks' bars, design §12), pure.
 *
 * A rhythm is a list of events, each a note or rest with its value, dots,
 * tie and tuplet. Lengths are exact fractions of a whole note (`Frac`, see
 * `meter.ts`). The compact text form (`parseRhythm`) is what the cell
 * catalogue and tests are written in:
 *
 * ```
 * 4      quarter          8.   dotted eighth      r8   eighth rest
 * 4~     quarter tied into the next note
 * 3[8 8 8]               a triplet: three eighths in the time of two
 * 2:3[8 8]               a duplet: two eighths in the time of three
 * ```
 *
 * Onsets feed the timing engine (M8-01): `onsetPulses(rhythm, pulseLength(ts))`
 * gives each attack in pulses from the bar's start, so a bar starting at pulse
 * `p0` has targets `onsetPulses(…).map((p) => metronome.timeAtPulse(p0 + p))`,
 * which stays right through tempo changes. `onsetTimesMs` does the same at a
 * steady tempo.
 */

// ---------------------------------------------------------------------------
// Events

/** `num` notes in the time of `inSpaceOf`: a triplet is 3 in 2. */
export interface Tuplet {
  readonly num: number;
  readonly inSpaceOf: number;
}

export interface RhythmEvent {
  readonly value: NoteValue;
  readonly dots?: 0 | 1 | 2;
  readonly rest?: boolean;
  /** Tied into the next event, which then isn't attacked again. */
  readonly tie?: boolean;
  readonly tuplet?: Tuplet;
  /**
   * Opens a new tuplet bracket. Neighbouring events with the same ratio share
   * a bracket until the next start, so `3[8 8 8] 3[8 8 8]` is two triplets,
   * each checked and drawn on its own. Events built without any start share
   * one bracket per run of the same ratio.
   */
  readonly tupletStart?: boolean;
}

export type Rhythm = readonly RhythmEvent[];

                                                        

/** The length of one event, tuplet included: a triplet eighth is 1/12. */
                                                   
                                                 
                                                                                   
 

                                               
                                     
 

/** Where each event starts, from the start of the rhythm. */
                                                
                         
                
                      
                 
                                     
   
             
 

                                                                            
                                                                                   
 

/** Tuplet brackets, as `[from, to)` index pairs: a run of one ratio, split at each `tupletStart`. */
                                                    
                                      
                       
                          
                             
                                                                                                    
                               
                               
     
              
 

// ---------------------------------------------------------------------------
// Text form

/**
 * The largest power of two below `num`: a triplet is in 2, a quintuplet in 4.
 * 0 for a power of two (a duplet or quadruplet), which must say what it is in.
 */
                                                
                                                                       
 

                                         
                                               

                                                                                     
                            
                               
                                                                                      
                                         
                                                                         
          
          
                              
                                    
                                   
                                  
                                            
    
 

/** Reads the text form (see the header): `"8. 16"`, `"r8 8 3[8 8 8] 4~ 4"`. */
                                                          
                                                          
                                
                                 
                                                               
                             
                  
                                       
               
                                                                           
                                  
                                                                          
                                                        
                                                       
                                  
                          
                     
                         
     
                                    
                
                                                                   
                             
     
                                                                                              
                
                                                                               
                         
     
   
                                                               
                                                                                         
             
 

                                              
                                                                                       
 

/**
 * The text form of a rhythm. For a parsed rhythm, `parseRhythm(formatRhythm(r))`
 * gives `r` back; hand-built tuplets without `tupletStart` come back with the
 * same brackets, each now marked with its start.
 */
                                                 
                                      
                             
                                         
                            
                           
                                  
                                     
               
     
                                                                                                  
                                                                         
                
   
                         
 

                        
         
         
         
         
           
           
           
                                               

/** VexFlow's duration code for a value, as `<Staff>` takes it: 4 → "q". */
                                                                                         
                               
 

// ---------------------------------------------------------------------------
// Bar fill

export interface BarCheck {
  /** `exact` fills the bar, `short` leaves room, `over` runs past the barline. */
  readonly fill: 'exact' | 'short' | 'over';
  readonly length: Frac;
  readonly bar: Frac;
  /** What is left to fill; negative when over. */
  readonly remaining: Frac;
  /**
   * Events that hide a felt beat. One that starts off a beat must not run
   * across the next: in 6/8, the middle quarter of `4 4 4` crosses beat 2
   * (that is 3/4). Simple time allows the syncopation `8 4 8`, a note of one
   * beat from the half beat, except across the middle of a four-beat bar.
   * Where the beats are groups (compound time, odd meters in eighths), one
   * that starts on a beat and crosses another must end on a beat: the half
   * of `2 4` in 6/8 ends partway through beat 2.
   */
  readonly hiddenBeats: number[];
  /** First index of each tuplet bracket that doesn't add up to whole plain values. */
  readonly brokenTuplets: number[];
  /** Ties out of a rest or into a rest. */
  readonly badTies: number[];
  /** Exact, with no hidden beats, broken tuplets or bad ties. */
  readonly ok: boolean;
}

/**
 * Checks one bar of `r` against a time signature, for D29 and for bars the
 * drills generate. A note that starts on a beat may last whole beats; one
 * that starts between beats must end by the next (bar the syncopation, see
 * `hiddenBeats`), as notation shows the beat. In pulse-beat meters a note
 * from a beat may end mid-beat (`4. 8` in 4/4); in group-beat meters it may
 * not once it crosses a beat.
 */
                                                                  
                                 
                            
                                         
                                   
                                                                

                       
                                            
                                                     
                                                                             
                                
                                   
                       
                             
                               
                                    
                                                                                           
                                     
                                                   
                                                                                          
                                                          
             
     
                       
                   
                                           
                                                        
                                          
     

                                     
                                                                       
                           

                               
                       
                                                             
     

          
         
           
        
              
                
                  
            
                                                                                            
    
 

// ---------------------------------------------------------------------------
// Beaming

/**
 * Which events beam together, as index groups of two or more. Eighths and
 * shorter beam within each of the meter's beam spans (`beamSpans`); a rest,
 * a quarter or longer, a change of tuplet, or a note running out of its span
 * breaks the beam.
 */
                                                                      
                           
                
                                  
                        
                   
   
                                                                            

                                
                             
                       
                       
                                                  
                 
    
                                
                       
                             
                                               
                               
                                                              
                                         
              
             
     
                                         
                                                                                      
                    
                       
     
          
                
 

// ---------------------------------------------------------------------------
// Onsets

/** An attack: the event that starts it, when, and how long it sounds (through its ties). */
export interface Onset {
  readonly index: number;
  readonly at: Frac;
  readonly length: Frac;
}

/**
 * The attacks of a rhythm, in order. Rests have none, and a note tied from
 * the one before isn't attacked again: its length joins that note's.
 */
                                            
                                
                                                              
                       
                       
                               
                          
                            
                                                                                 
                                                             
     
             
 

/** Each attack in pulses from the start (a pulse of 1/8 makes a dotted quarter 3). */
                                                               
                                                                  
 

export interface OnsetTiming {
  /** Pulses per minute, as the metronome counts them. */
  readonly bpm: number;
  /** The pulse's length; a quarter unless given (`pulseLength(ts)` for a meter). */
  readonly pulse?: Frac;
  /** When the rhythm starts. */
  readonly startMs?: number;
}

/** Each attack in ms at a steady tempo: the targets `gradeTiming` aligns onsets to. */
                             
            
                                                        
             
                                                                                               
                          
                                                            
 

// ---------------------------------------------------------------------------
// Counting

                                        
                    
                    
                    
                       
                      
  

                                          
                     
                     
                     
                     
                     
  

/**
 * What each event is counted as: "1 e & a" in simple time, "1 la li" in
 * compound time, with beat numbers for the beats. An event between the
 * counted subdivisions gets "". Odd-meter groups count like a simple beat
 * (two pulses) or a compound one (three).
 */
                                                                        
                       
                            
                                    
                                       
                                                                                            
              
                                                                              
                        
                                                                 
                                          
                                                                    
                                                          
     
 

// ---------------------------------------------------------------------------
// Rhythm cells

/** Cells for beats split in two (`simple`) or in three (`compound`). */
export type CellFeel = 'simple' | 'compound';

export interface RhythmCell {
  /** The item ID suffix: `dotted-8th-16th`. */
  readonly slug: string;
  /** `rhythm:dotted-8th-16th`. */
  readonly id: string;
  readonly name: string;
  readonly feel: CellFeel;
  /** The text form (`parseRhythm`). */
  readonly notation: string;
  readonly rhythm: Rhythm;
  /** Felt beats it lasts: quarters for simple cells, dotted quarters for compound. */
  readonly beats: number;
  /** Teaching order (research/03 R4): 1 quarters and eighths, 2 sixteenths, 3 dotted, 4 tuplets, 5 syncopation. */
  readonly stage: 1 | 2 | 3 | 4 | 5;
  /** The cell it is most easily confused with, to interleave (dotted vs straight, design §12). */
  readonly confusable?: string;
}

type CellSpec = Omit<RhythmCell, 'id' | 'rhythm' | 'slug'>;

                                              
                                           
                                                                                  
                                                                                               
               
                        
                   
                    
             
             
                                  
    
                                                                            
                 
                            
                   
                            
             
             
    
                    
                                      
                   
                        
             
             
                                
    
                    
                                         
                   
                        
             
             
                                
    
                      
                                        
                   
                      
             
             
                           
    
                      
                                        
                   
                      
             
             
                                  
    
                         
                                      
                   
                     
             
             
                               
    
                                                                                                
                  
                                
                   
                         
             
             
                           
    
                      
                                 
                   
                         
             
             
                               
    
                  
                                   
                   
                     
             
             
                           
    
                    
                                         
                   
                        
             
             
                             
    
                      
                                    
                   
                      
             
             
                               
    
                                       
                     
                           
                     
                   
             
             
    
                 
                          
                     
                      
             
             
                              
    
                  
                               
                     
                    
             
             
                              
    
                  
                               
                     
                    
             
             
                              
    
                
                           
                     
                                  
             
             
    
              
                                             
                     
                        
             
             
                             
    
           
                   
                     
                         
             
             
                              
    
  

export function rhythmCellId(slug: string): string {
  return `rhythm:${slug}`;
}

/** The catalogue, in teaching order then catalogue order. */
                                                                             
                           
         
                           
                                       
            
     
                                     

                                                              

/** A cell by slug or ID: `dotted-8th-16th` or `rhythm:dotted-8th-16th`. */
                                                          
                                                             
                                                                      
              
 

/** A cell's length: quarters per beat for simple cells, dotted quarters for compound. */
                                                    
                                   
 

/**
 * The slots a meter offers cells: a simple slot per quarter of a beat made of
 * quarters, a compound slot per dotted-quarter beat. `null` marks a beat no
 * cell fits (3/8's eighth beats, 6/4's dotted halves).
 */
                                                            
                             
                                                           
                                                                                        
                                                   
                                                                                  
     
 

/**
 * Cells in sequence as one bar. Simple cells take quarter slots and compound
 * cells dotted-quarter beats, so in 7/8 (2+2+3) two simple one-beat cells and
 * then a compound one fill a bar. The cells must fill the bar exactly, and
 * the result must pass `checkBar` (no syncopation across the middle of 4/4).
 */
                                                                                              
                              
                                        
             
                                
                             
                                                   
                                  
                                                                            
                                           
                                                                                 
                     
                             
   
                                                                                            
                                                                                      
             
 

/** One cell repeated to fill a bar (`two-8ths` in 4/4 is eight eighths). */
                                                                             
                                                         
                    
                                                                                        
                                                                 
 

/** The cells that can fill bars of a meter by repetition. */
                                                           
                                     
         
                     
                  
             
                   
     
     
 
